import React, { useState, useEffect, useCallback } from 'react';
import { getActiveUsersTimeSeries } from '../../services/api';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
} from 'recharts';

function ReportsActiveUsers() {
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getActiveUsersTimeSeries(days);
      setTimeSeriesData(result.time_series || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch active users data');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDaysChange = (e) => {
    setDays(Number(e.target.value));
  };

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading active users data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <p>{error}</p>
        <button className="refresh-btn" onClick={fetchData}>Retry</button>
      </div>
    );
  }

  // --- Derived data ---

  const latestPoint = timeSeriesData.length > 0
    ? timeSeriesData[timeSeriesData.length - 1]
    : null;

  const stickinessRatio = latestPoint && latestPoint.mau > 0
    ? ((latestPoint.dau / latestPoint.mau) * 100)
    : 0;

  const getStickinessColor = (ratio) => {
    if (ratio > 20) return '#10b981';
    if (ratio >= 10) return '#f59e0b';
    return '#ef4444';
  };

  const getStickinessLabel = (ratio) => {
    if (ratio > 20) return 'Good';
    if (ratio >= 10) return 'Moderate';
    return 'Low';
  };

  // Summary stats
  const peakDauEntry = timeSeriesData.reduce(
    (max, entry) => (entry.dau > (max?.dau || 0) ? entry : max),
    null
  );
  const averageDau = timeSeriesData.length > 0
    ? (timeSeriesData.reduce((sum, entry) => sum + entry.dau, 0) / timeSeriesData.length)
    : 0;
  const peakEventsEntry = timeSeriesData.reduce(
    (max, entry) => (entry.events > (max?.events || 0) ? entry : max),
    null
  );
  const totalEvents = timeSeriesData.reduce((sum, entry) => sum + entry.events, 0);

  // Format date for X-axis
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}`;
    }
    return dateStr;
  };

  // Custom tooltip for the main chart
  const MainChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '6px',
        padding: '10px 14px',
        color: '#fff',
        fontSize: '13px',
      }}>
        <p style={{ margin: '0 0 6px 0', fontWeight: 600, color: '#ccc' }}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '3px 0', color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  };

  // Custom tooltip for the events chart
  const EventsChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '6px',
        padding: '10px 14px',
        color: '#fff',
        fontSize: '13px',
      }}>
        <p style={{ margin: '0 0 6px 0', fontWeight: 600, color: '#ccc' }}>
          {label}
        </p>
        <p style={{ margin: '3px 0', color: '#f59e0b' }}>
          Events: {payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  };

  // Chart data with formatted dates for display
  const chartData = timeSeriesData.map((entry) => ({
    ...entry,
    dateLabel: formatDate(entry.date),
  }));

  return (
    <div className="reports-active-users">
      {/* Header with period selector */}
      <div className="reports-section-header">
        <h3>Active Users Analytics</h3>
        <div className="period-selector">
          <label htmlFor="active-users-period">Time Period:</label>
          <select
            id="active-users-period"
            value={days}
            onChange={handleDaysChange}
            className="period-select"
          >
            <option value={30}>Last 30 Days</option>
            <option value={60}>Last 60 Days</option>
            <option value={90}>Last 90 Days</option>
            <option value={180}>Last 180 Days</option>
          </select>
          <button className="refresh-btn" onClick={fetchData} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {/* Stickiness Metric Card */}
      <div className="analytics-kpi-card" style={{
        backgroundColor: '#1a1a1a',
        border: `1px solid ${getStickinessColor(stickinessRatio)}`,
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        maxWidth: '320px',
      }}>
        <div style={{ color: '#999', fontSize: '13px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          DAU / MAU Stickiness
        </div>
        <div style={{ fontSize: '36px', fontWeight: 700, color: getStickinessColor(stickinessRatio) }}>
          {stickinessRatio.toFixed(1)}%
        </div>
        <div style={{ color: '#999', fontSize: '13px', marginTop: '4px' }}>
          {getStickinessLabel(stickinessRatio)} stickiness
          {latestPoint && (
            <span style={{ color: '#666', marginLeft: '8px' }}>
              (DAU: {latestPoint.dau}, MAU: {latestPoint.mau})
            </span>
          )}
        </div>
        <div style={{ color: '#666', fontSize: '11px', marginTop: '8px' }}>
          Good stickiness &gt; 20%
        </div>
      </div>

      {/* Main Chart: DAU / WAU / MAU */}
      <div className="analytics-chart-container" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
      }}>
        <h4 style={{ color: '#fff', margin: '0 0 16px 0', fontSize: '15px' }}>
          Daily / Weekly / Monthly Active Users
        </h4>
        {chartData.length === 0 ? (
          <p className="no-data">No time series data available for the selected period.</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="dateLabel"
                tick={{ fill: '#999', fontSize: 12 }}
                tickLine={{ stroke: '#555' }}
                axisLine={{ stroke: '#555' }}
              />
              <YAxis
                tick={{ fill: '#999', fontSize: 12 }}
                tickLine={{ stroke: '#555' }}
                axisLine={{ stroke: '#555' }}
              />
              <Tooltip content={<MainChartTooltip />} />
              <Legend
                wrapperStyle={{ color: '#ccc', fontSize: '13px' }}
              />
              <Bar
                dataKey="dau"
                name="DAU"
                fill="#218aff"
                opacity={0.6}
                radius={[2, 2, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="wau"
                name="WAU"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#10b981' }}
              />
              <Line
                type="monotone"
                dataKey="mau"
                name="MAU"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#8b5cf6' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Events Volume Chart */}
      <div className="analytics-chart-container" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
      }}>
        <h4 style={{ color: '#fff', margin: '0 0 16px 0', fontSize: '15px' }}>
          Events Volume by Day
        </h4>
        {chartData.length === 0 ? (
          <p className="no-data">No events data available for the selected period.</p>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="dateLabel"
                tick={{ fill: '#999', fontSize: 12 }}
                tickLine={{ stroke: '#555' }}
                axisLine={{ stroke: '#555' }}
              />
              <YAxis
                tick={{ fill: '#999', fontSize: 12 }}
                tickLine={{ stroke: '#555' }}
                axisLine={{ stroke: '#555' }}
              />
              <Tooltip content={<EventsChartTooltip />} />
              <Bar
                dataKey="events"
                name="Events"
                fill="#f59e0b"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
      }}>
        <div className="analytics-kpi-card" style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <div style={{ color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
            Peak DAU
          </div>
          <div style={{ color: '#218aff', fontSize: '28px', fontWeight: 700 }}>
            {peakDauEntry ? peakDauEntry.dau.toLocaleString() : '0'}
          </div>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
            {peakDauEntry ? formatDate(peakDauEntry.date) : '--'}
          </div>
        </div>

        <div className="analytics-kpi-card" style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <div style={{ color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
            Average DAU
          </div>
          <div style={{ color: '#10b981', fontSize: '28px', fontWeight: 700 }}>
            {averageDau.toFixed(1)}
          </div>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
            Over {days} days
          </div>
        </div>

        <div className="analytics-kpi-card" style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <div style={{ color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
            Peak Events Day
          </div>
          <div style={{ color: '#f59e0b', fontSize: '28px', fontWeight: 700 }}>
            {peakEventsEntry ? peakEventsEntry.events.toLocaleString() : '0'}
          </div>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
            {peakEventsEntry ? formatDate(peakEventsEntry.date) : '--'}
          </div>
        </div>

        <div className="analytics-kpi-card" style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <div style={{ color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
            Total Events
          </div>
          <div style={{ color: '#8b5cf6', fontSize: '28px', fontWeight: 700 }}>
            {totalEvents.toLocaleString()}
          </div>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
            Over {days} days
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportsActiveUsers;
